import { inject, signal, WritableSignal, Service } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { DataAccessPsService } from '@h5s/data-access-ps';
import { DataAccessPurchasedStayService } from '@h5s/data-access-reservation';
import { DialogService, FILTER_QUERY_PARAM_PREFIX, ModalService, ToastService } from '@h5s/design-system';
import { Customer, OwnerPublicSecurity, PsCustomer, ResourceValidationResult, UpdateCustomer } from '@h5s/entities';
import { FeatureDocService } from '@h5s/feature-doc';
import { isBeforeDateTime, today } from '@h5s/util-date';
import { ActionType, OverlayResult } from '@h5s/util-overlay';
import { getDateTimeParser, getNumberParser } from '@h5s/util-parser';
import { mapToRecord } from '@h5s/util-rxjs';
import { DateTime } from 'luxon';
import { BehaviorSubject, catchError, filter, map, Observable, of, Subject, tap } from 'rxjs';
import { DownloadPsReceiptModalParams } from './feature-ps/components/ps-download-receipt/models/download-ps-receipt-modal-params';
import { DownloadPsReceiptModalResult } from './feature-ps/components/ps-download-receipt/models/download-ps-receipt-modal-result';
import { PsDownloadReceiptComponent } from './feature-ps/components/ps-download-receipt/ps-download-receipt.component';
import { PsFilterModel } from './feature-ps/components/ps-filter/models/ps-filters-model';
import { TablePsCustomer } from './feature-ps/components/ps-table/models/table-ps-customer';
import { FEATURE_PS_TRANSLATIONS } from './feature-ps/translations/feature-ps.translations';
import { HttpResponse } from '@angular/common/http';

const PS_FILTERS_DEFAULTS: PsFilterModel = {
  date: today(),
  profile: -1,
};

@Service()
export class FeaturePsService {
  private readonly dataAccessPsService = inject(DataAccessPsService);
  private readonly dataAccessPurchasedStayService = inject(DataAccessPurchasedStayService);
  private readonly toastService = inject(ToastService);
  private readonly featureDocService = inject(FeatureDocService);
  private readonly modalService = inject(ModalService);
  private readonly dialogService = inject(DialogService);
  private readonly router = inject(Router);

  /* Translations */
  public readonly translation = FEATURE_PS_TRANSLATIONS;

  public selectReadyPsCustomer = new Subject<void>();
  public research = new Subject<void>();
  public showStatus = new BehaviorSubject<boolean>(false);
  public ownerPublicSecurity: OwnerPublicSecurity[] | null = null;
  public readonly selectedTablePsCustomers: WritableSignal<TablePsCustomer[]> = signal([]);

  private readonly MINIMUM_DAYS_RANGE_FOR_RECEIPT = 30;

  public getOwnerPublicSecurity(): Observable<OwnerPublicSecurity[]> {
    return this.ownerPublicSecurity ? of(this.ownerPublicSecurity) : this.dataAccessPsService.getOwnerPublicSecurity();
  }

  public hasOwnerPublicSecurity(): Observable<boolean> {
    return this.getOwnerPublicSecurity().pipe(map((ops) => ops.length > 0));
  }

  public getOwnerPublicSecurityRecord(): Observable<Record<number, string>> {
    return this.getOwnerPublicSecurity().pipe(
      tap((ops) => {
        this.showStatus.next(ops.some((op) => op.sendOnline));
        this.ownerPublicSecurity = ops;
      }),
      mapToRecord('id', 'description'),
    );
  }

  public getOwnerPublicSecurityById(id: number): Observable<OwnerPublicSecurity> {
    const ownerPublicSecurity$ = this.ownerPublicSecurity
      ? of(this.ownerPublicSecurity)
      : this.dataAccessPsService.getOwnerPublicSecurity();

    return ownerPublicSecurity$.pipe(
      map((ops) => ops.find((op) => op.id === id)),
      filter((op): op is NonNullable<typeof op> => !!op),
    );
  }

  public getFilteredPsCustomers(date: DateTime, ownerPublicSecurityId: number): Observable<PsCustomer[]> {
    return this.dataAccessPsService.getPsCustomer(date, ownerPublicSecurityId);
  }

  public getReservationIdByPurchasedStay(id: number): Observable<number> {
    return this.dataAccessPurchasedStayService.getPurchasedStay(id).pipe(
      map((ps) => ps.reservationId),
      filter((reservationId): reservationId is number => reservationId !== null && reservationId !== undefined),
    );
  }

  public sendPsCustomers(date: DateTime, profile: number): Observable<boolean> {
    const selectedTablePsCustomers = this.selectedTablePsCustomers();
    const purchasedStayIds: number[] = selectedTablePsCustomers
      .map((tablePsCustomer) => tablePsCustomer.purchasedStayToCustomer.id)
      .filter((id): id is number => id !== null && id !== undefined);

    return this.dataAccessPsService.sendPsCustomers(date, profile, purchasedStayIds).pipe(
      tap(() => {
        this.research.next();
        this.toastService.showSuccess(this.translation.toastSendPSCustomerOk);
      }),
      catchError((error) => {
        throw error;
      }),
    );
  }

  public validateCustomerForPs(customer: UpdateCustomer): Observable<ResourceValidationResult<Customer>[]> {
    return this.dataAccessPsService.validateCustomerForPs(customer);
  }

  public downloadPsFile(date: DateTime, profile: number): Observable<HttpResponse<Blob>> {
    const selectedTablePsCustomers = this.selectedTablePsCustomers();
    const purchasedStayIds: number[] = selectedTablePsCustomers
      .map((tablePsCustomer) => tablePsCustomer.purchasedStayToCustomer.id)
      .filter((id): id is number => id !== null && id !== undefined);

    return this.featureDocService.openPsFile(profile, date, purchasedStayIds);
  }

  public openDownloadReceiptModal(date: DateTime, profile: number): void {
    this.modalService
      .show<DownloadPsReceiptModalResult>({
        component: PsDownloadReceiptComponent,
        title: $localize`:@@ps.title.downloadPsReceipt:Scarica ricevuta di invio PS`,
        confirmButtonText: $localize`:@@common.button.confirm:Conferma`,
        cancelButtonText: $localize`:@@common.button.cancel:Annulla`,
        locals: new DownloadPsReceiptModalParams(date),
      })
      .subscribe((response: OverlayResult<DownloadPsReceiptModalResult>) => {
        if (response.actionType !== ActionType.Confirm || !response.data) {
          return;
        }

        this.downloadPsReceipt(date, profile, response.data?.selectedDate);
      });
  }

  /**
   * Controlla se è possibile navigare alla schermata della PS.
   */
  public validatePsForNavigation(
    profileOpts: Record<number, string>,
    route: ActivatedRouteSnapshot,
  ): UrlTree | boolean {
    const profileQP = route.queryParams[FILTER_QUERY_PARAM_PREFIX + 'profile'];
    const dateQP = String(route.queryParams[FILTER_QUERY_PARAM_PREFIX + 'date']);

    const hasPublicSecurityProfiles = profileOpts && Object.keys(profileOpts).length > 0;
    if (!hasPublicSecurityProfiles) {
      this.dialogService.showAlert({ content: this.translation.alertNoPublicSecurity });
      return false;
    }

    if (profileQP && dateQP) return true;

    if (dateQP) PS_FILTERS_DEFAULTS.date = getDateTimeParser(PS_FILTERS_DEFAULTS.date).parse(dateQP);

    if (!profileQP)
      PS_FILTERS_DEFAULTS.profile = getNumberParser(PS_FILTERS_DEFAULTS.profile).parse(Object.keys(profileOpts)[0]);
    else PS_FILTERS_DEFAULTS.profile = getNumberParser(PS_FILTERS_DEFAULTS.profile).parse(profileQP);

    const queryParams: Record<string, string> = {};

    const dateSerialized = getDateTimeParser().serialize(PS_FILTERS_DEFAULTS.date);
    if (dateSerialized) queryParams[FILTER_QUERY_PARAM_PREFIX + 'date'] = dateSerialized;

    const profileSerialized = getNumberParser().serialize(PS_FILTERS_DEFAULTS.profile);
    if (profileSerialized) queryParams[FILTER_QUERY_PARAM_PREFIX + 'profile'] = profileSerialized;

    const urlTree: UrlTree = this.router.createUrlTree(['/ps'], {
      queryParams,
      queryParamsHandling: 'replace',
    });
    return urlTree; // Ritornando un UrlTree, Angular Router sa che deve navigare
  }

  private downloadPsReceipt(date: DateTime, profile: number, selectedDate: DateTime): void {
    if (isBeforeDateTime(selectedDate, date.minus(this.MINIMUM_DAYS_RANGE_FOR_RECEIPT))) {
      this.dialogService.showAlert({
        content: $localize`:@@ps.message.receiptTooOld:La ricevuta può essere scaricata solo per gli ultimi 30 giorni.`,
      });
      return;
    }

    this.featureDocService.openPsReceipt(profile, selectedDate);
  }
}
